export const translations = {
  en: {
    nav: {
      how: "How it works",
      search: "Cognitive Search",
      results: "Results",
      technology: "Technology",
      launch: "Launch",
    },
    hero: {
      kicker: "Rexxengine — part of the Rexxpons family",
      titleLine1: "Cognitive",
      titleLine2Prefix: "Search ",
      titleLine2Highlight: "Engine",
      subtitle:
        "Beyond search, toward understanding. Rexxengine interprets your query like a mind and returns structured, reasoned knowledge.",
      try: "Try",
    },
    suggestions: [
      "Explain quantum error correction",
      "Compare RAG vs fine-tuning",
      "https://arxiv.org/abs/1706.03762",
      "What causes inflation cycles?",
    ],
    search: {
      placeholder: "Paste a link or ask a question…",
      reason: "Reason",
      micStart: "Speak your question",
      micStop: "Stop listening",
      micUnsupported: "Voice input isn't supported in this browser.",
      micError: "Couldn't hear that — please try again.",
      language: "Language",
      reasoning: "Reasoning",
      attitude: "Attitude",
      opt: {
        Auto: "Auto",
        IT: "IT",
        EN: "EN",
        Multi: "Multi",
        Fast: "Fast",
        Balanced: "Balanced",
        Deep: "Deep",
        Neutral: "Neutral",
        Creative: "Creative",
        Critical: "Critical",
      },
    },
    panel: {
      kicker: "/ The Cognitive Loop",
      titleA: "Three cognitive stages,",
      titleB: " one query.",
      cards: [
        {
          tag: "01 — Interpret",
          title: "Intent Understanding",
          desc: "Rexxengine decodes what you actually mean — parsing entities, constraints and the shape of the answer you need before a single source is touched.",
        },
        {
          tag: "02 — Retrieve",
          title: "Semantic Retrieval",
          desc: "A hybrid engine fuses lexical precision with dense semantic recall, surfacing sources by meaning and explanatory depth — never keyword noise.",
        },
        {
          tag: "03 — Connect",
          title: "Cognitive Link Generation",
          desc: "Each result is rebuilt as a cognitive link: summarised, tagged, scored for reliability and wired into a live knowledge graph of relations.",
        },
      ],
    },
    how: {
      kicker: "/ How Rexxengine Works",
      title: "A manifesto in four movements.",
      chapters: [
        { title: "Parse the intent", body: "Rexxengine reads your query the way a researcher reads a brief — extracting entities, intent type and the answer's expected shape. A link and a question are treated as the same thing: a request to understand." },
        { title: "Retrieve by meaning", body: "A hybrid retriever runs lexical and semantic passes in parallel across the corpus, ranking candidates by explanatory depth and conceptual proximity rather than surface term frequency." },
        { title: "Reason over the graph", body: "Retrieved evidence is projected into a knowledge graph. Rexxengine traces relations between concepts, resolves contradictions and clusters sources into coherent lines of reasoning." },
        { title: "Return cognitive links", body: "Every answer is composed as structured cognitive links — summary, key concepts, reliability score, relation graph and source — so you leave with understanding, not a list of tabs." },
      ],
    },
    preview: {
      kicker: "/ Cognitive Results",
      titleA: "Results that think",
      titleB: " back.",
      cta: "View full results page",
      recognizedIntent: "Recognized Intent",
      entities: "Entities",
      confidence: "confidence",
      sample: {
        intentLabel: "Comparative Research",
        entities: ["Transformers", "Self-Attention", "Context Windows", "Embeddings"],
        title: "Attention Is All You Need — The Architectural Foundation",
        summary:
          "The seminal work replacing recurrence with pure self-attention. Every token attends to every other token in parallel, weighted by learned query-key similarity. Multi-head attention captures different relational subspaces at once, and the result is a model that understands context through weighted relationships rather than sequential memory.",
        concepts: ["Self-Attention", "Multi-Head", "Positional Encoding"],
      },
    },
    graph: {
      kicker: "/ Knowledge Graph",
      titleA: "Every answer is a",
      titleB: " living map.",
      desc: "Rexxengine doesn't return a list — it returns a structure. Concepts become nodes, relations become edges, and reasoning becomes something you can see and navigate.",
      points: [
        "Neon-cyan concept nodes with live glow on activation",
        "Electric-blue edges trace reasoning between concepts",
        "Hover any node to illuminate its relational neighbourhood",
      ],
      activeNode: "active node",
    },
    tech: {
      kicker: "/ Technology & Architecture",
      titleA: "An engine built to",
      titleB: " reason.",
      core: "Rexxpons Cognitive Core",
      flow: "Retrieval → Reasoning → Ranking",
      pillars: [
        { title: "Hybrid Retrieval", desc: "Lexical BM25 precision fused with dense vector recall — two passes, one ranked candidate set." },
        { title: "Cognitive Intent Analysis", desc: "A reasoning layer classifies intent, extracts entities and predicts the answer's ideal structure." },
        { title: "Knowledge Graph Reasoning", desc: "Evidence is projected into a graph where relations are traced and contradictions resolved." },
        { title: "Cognitive Ranking Model", desc: "Sources are scored on explanatory depth and reliability, not keyword frequency." },
      ],
    },
    brand: {
      kicker: "/ Brand Family",
      titleA: "Powered by the",
      titleB: "Cognitive Framework",
      desc: "Rexxengine is one instrument in the Rexxpons family — a shared foundation for machines that understand.",
    },
    footer: {
      tagline: "Beyond search, toward understanding. A cognitive search engine from the Rexxpons family.",
      digest: "Cognitive digest",
      join: "Join",
      emailPlaceholder: "you@lab.io",
      cols: {
        Product: ["Cognitive Search", "Knowledge Graph", "Results Engine", "API Access"],
        Framework: ["Rexxpons Core", "Retrieval", "Reasoning", "Ranking"],
        Company: ["About", "Research", "Careers", "Contact"],
      },
      colTitles: { Product: "Product", Framework: "Framework", Company: "Company" },
      rights: "Rexxpons Cognitive Framework.",
      privacy: "Privacy",
      terms: "Terms",
      status: "Systems nominal",
      network: "Rexxpons Network",
      networkTagline: "The connected network of legal, risk and AI products.",
      appsTitle: "Network Apps",
      by: "by L.G. Production",
    },
    card: {
      keyConcepts: "Key concepts",
      reliability: "Reliability",
      relations: "Relations",
      openSource: "Open Source",
      exploreRelations: "Explore Relations",
    },
    loader: {
      reasoning: "Rexxengine is reasoning",
      stages: ["Parsing intent", "Retrieving by meaning", "Reasoning over the graph", "Composing cognitive links"],
    },
    results: {
      home: "Home",
      resultsFor: "Cognitive results for",
      linksRanked: "cognitive links · ranked by depth",
      live: "live",
      recognizedIntent: "Recognized Intent",
      confidence: "confidence",
      cognitiveSummary: "Cognitive Summary",
      cognitiveFilters: "Cognitive Filters",
      reasoningMode: "Reasoning Mode",
      attitude: "Attitude",
      depthLevel: "Depth Level",
      conceptClusters: "Concept Clusters",
      graphNavigation: "Graph Navigation",
      opt: {
        Fast: "Fast", Balanced: "Balanced", Deep: "Deep",
        Neutral: "Neutral", Creative: "Creative", Critical: "Critical",
        Overview: "Overview", Standard: "Standard", Exhaustive: "Exhaustive",
      },
      toastError: "Cognitive engine hiccup — showing a sample result.",
    },
  },

  it: {
    nav: {
      how: "Come funziona",
      search: "Ricerca Cognitiva",
      results: "Risultati",
      technology: "Tecnologia",
      launch: "Avvia",
    },
    hero: {
      kicker: "Rexxengine — parte della famiglia Rexxpons",
      titleLine1: "Motore di",
      titleLine2Prefix: "Ricerca ",
      titleLine2Highlight: "Cognitiva",
      subtitle:
        "Oltre la ricerca, verso la comprensione. Rexxengine interpreta la tua domanda come una mente e restituisce conoscenza strutturata e ragionata.",
      try: "Prova",
    },
    suggestions: [
      "Spiega la correzione degli errori quantistici",
      "Confronta RAG e fine-tuning",
      "https://arxiv.org/abs/1706.03762",
      "Cosa causa i cicli di inflazione?",
    ],
    search: {
      placeholder: "Incolla un link o fai una domanda…",
      reason: "Ragiona",
      micStart: "Pronuncia la tua domanda",
      micStop: "Interrompi ascolto",
      micUnsupported: "L'input vocale non è supportato in questo browser.",
      micError: "Non ho sentito bene — riprova.",
      language: "Lingua",
      reasoning: "Ragionamento",
      attitude: "Atteggiamento",
      opt: {
        Auto: "Auto",
        IT: "IT",
        EN: "EN",
        Multi: "Multi",
        Fast: "Rapido",
        Balanced: "Bilanciato",
        Deep: "Profondo",
        Neutral: "Neutro",
        Creative: "Creativo",
        Critical: "Critico",
      },
    },
    panel: {
      kicker: "/ Il Ciclo Cognitivo",
      titleA: "Tre fasi cognitive,",
      titleB: " una domanda.",
      cards: [
        {
          tag: "01 — Interpreta",
          title: "Comprensione dell'Intento",
          desc: "Rexxengine decodifica cosa intendi davvero — analizzando entità, vincoli e la forma della risposta che ti serve prima ancora di toccare una fonte.",
        },
        {
          tag: "02 — Recupera",
          title: "Recupero Semantico",
          desc: "Un motore ibrido fonde la precisione lessicale con il richiamo semantico denso, facendo emergere le fonti per significato e profondità — mai per rumore di parole chiave.",
        },
        {
          tag: "03 — Connetti",
          title: "Generazione di Link Cognitivi",
          desc: "Ogni risultato viene ricostruito come link cognitivo: riassunto, etichettato, valutato per affidabilità e collegato a un knowledge graph di relazioni.",
        },
      ],
    },
    how: {
      kicker: "/ Come funziona Rexxengine",
      title: "Un manifesto in quattro movimenti.",
      chapters: [
        { title: "Analizza l'intento", body: "Rexxengine legge la tua domanda come un ricercatore legge un brief — estraendo entità, tipo di intento e la forma attesa della risposta. Un link e una domanda sono la stessa cosa: una richiesta di comprensione." },
        { title: "Recupera per significato", body: "Un recuperatore ibrido esegue passaggi lessicali e semantici in parallelo sul corpus, ordinando i candidati per profondità esplicativa e prossimità concettuale, non per frequenza dei termini." },
        { title: "Ragiona sul grafo", body: "Le prove recuperate vengono proiettate in un knowledge graph. Rexxengine traccia le relazioni tra concetti, risolve le contraddizioni e raggruppa le fonti in linee di ragionamento coerenti." },
        { title: "Restituisce link cognitivi", body: "Ogni risposta è composta da link cognitivi strutturati — sintesi, concetti chiave, punteggio di affidabilità, grafo delle relazioni e fonte — così esci con la comprensione, non con una lista di schede." },
      ],
    },
    preview: {
      kicker: "/ Risultati Cognitivi",
      titleA: "Risultati che ti",
      titleB: " rispondono.",
      cta: "Vai alla pagina completa dei risultati",
      recognizedIntent: "Intento Riconosciuto",
      entities: "Entità",
      confidence: "affidabilità",
      sample: {
        intentLabel: "Ricerca Comparativa",
        entities: ["Transformer", "Self-Attention", "Finestre di Contesto", "Embedding"],
        title: "Attention Is All You Need — La Base Architetturale",
        summary:
          "L'opera fondamentale che sostituisce la ricorrenza con la pura self-attention. Ogni token presta attenzione a ogni altro token in parallelo, pesato dalla similarità query-key appresa. L'attenzione multi-testa cattura diversi sottospazi relazionali contemporaneamente: il risultato è un modello che comprende il contesto tramite relazioni pesate anziché memoria sequenziale.",
        concepts: ["Self-Attention", "Multi-Head", "Positional Encoding"],
      },
    },
    graph: {
      kicker: "/ Knowledge Graph",
      titleA: "Ogni risposta è una",
      titleB: " mappa viva.",
      desc: "Rexxengine non restituisce una lista — restituisce una struttura. I concetti diventano nodi, le relazioni diventano archi, e il ragionamento diventa qualcosa che puoi vedere e navigare.",
      points: [
        "Nodi concetto ciano al neon con bagliore all'attivazione",
        "Archi blu elettrico tracciano il ragionamento tra i concetti",
        "Passa su un nodo per illuminare il suo vicinato relazionale",
      ],
      activeNode: "nodo attivo",
    },
    tech: {
      kicker: "/ Tecnologia e Architettura",
      titleA: "Un motore costruito per",
      titleB: " ragionare.",
      core: "Rexxpons Cognitive Core",
      flow: "Recupero → Ragionamento → Ranking",
      pillars: [
        { title: "Recupero Ibrido", desc: "Precisione lessicale BM25 fusa con richiamo vettoriale denso — due passaggi, un unico insieme ordinato di candidati." },
        { title: "Analisi Cognitiva dell'Intento", desc: "Uno strato di ragionamento classifica l'intento, estrae le entità e prevede la struttura ideale della risposta." },
        { title: "Ragionamento su Knowledge Graph", desc: "Le prove vengono proiettate in un grafo dove le relazioni sono tracciate e le contraddizioni risolte." },
        { title: "Modello di Ranking Cognitivo", desc: "Le fonti sono valutate per profondità esplicativa e affidabilità, non per frequenza di parole chiave." },
      ],
    },
    brand: {
      kicker: "/ Famiglia del Brand",
      titleA: "Basato sul",
      titleB: "Cognitive Framework",
      desc: "Rexxengine è uno strumento della famiglia Rexxpons — una base condivisa per macchine che comprendono.",
    },
    footer: {
      tagline: "Oltre la ricerca, verso la comprensione. Un motore di ricerca cognitivo della famiglia Rexxpons.",
      digest: "Digest cognitivo",
      join: "Iscriviti",
      emailPlaceholder: "tu@lab.io",
      cols: {
        Product: ["Ricerca Cognitiva", "Knowledge Graph", "Motore Risultati", "Accesso API"],
        Framework: ["Rexxpons Core", "Recupero", "Ragionamento", "Ranking"],
        Company: ["Chi siamo", "Ricerca", "Lavora con noi", "Contatti"],
      },
      colTitles: { Product: "Prodotto", Framework: "Framework", Company: "Azienda" },
      rights: "Rexxpons Cognitive Framework.",
      privacy: "Privacy",
      terms: "Termini",
      status: "Sistemi operativi",
      network: "Rete Rexxpons",
      networkTagline: "La rete connessa di prodotti legali, di risk e AI.",
      appsTitle: "App del Network",
      by: "by L.G. Production",
    },
    card: {
      keyConcepts: "Concetti chiave",
      reliability: "Affidabilità",
      relations: "Relazioni",
      openSource: "Apri Fonte",
      exploreRelations: "Esplora Relazioni",
    },
    loader: {
      reasoning: "Rexxengine sta ragionando",
      stages: ["Analisi dell'intento", "Recupero per significato", "Ragionamento sul grafo", "Composizione dei link cognitivi"],
    },
    results: {
      home: "Home",
      resultsFor: "Risultati cognitivi per",
      linksRanked: "link cognitivi · ordinati per profondità",
      live: "live",
      recognizedIntent: "Intento Riconosciuto",
      confidence: "affidabilità",
      cognitiveSummary: "Sintesi Cognitiva",
      cognitiveFilters: "Filtri Cognitivi",
      reasoningMode: "Modalità Ragionamento",
      attitude: "Atteggiamento",
      depthLevel: "Livello di Profondità",
      conceptClusters: "Cluster di Concetti",
      graphNavigation: "Navigazione Grafo",
      opt: {
        Fast: "Rapido", Balanced: "Bilanciato", Deep: "Profondo",
        Neutral: "Neutro", Creative: "Creativo", Critical: "Critico",
        Overview: "Panoramica", Standard: "Standard", Exhaustive: "Esaustivo",
      },
      toastError: "Intoppo del motore cognitivo — mostro un risultato di esempio.",
    },
  },
};
